import React, { useEffect } from "react"
import ReactApexChart from "react-apexcharts"

/*
 * Shared "dash kit" for the overview dashboards (Owner / Finance / HRMS / Project).
 * One scoped stylesheet (.dk-* classes) + a few small presentational helpers so every
 * overview reads as one system. STATIC data lives in each dashboard; this file is
 * purely presentation. Wire real figures per module later.
 */

export const CUR = "AED"
export const fmtM = n => `${CUR} ${(n / 1_000_000).toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}M`
export const fmtN = n => Number(n).toLocaleString("en-AE")

export const PALETTE = {
  indigo: "#4f6ef7", green: "#22c55e", violet: "#8b5cf6", amber: "#f59e0b",
  cyan: "#06b6d4", red: "#ef4444", sky: "#0ea5e9", slate: "#64748b", teal: "#14b8a6",
}
export const SERIES_COLORS = ["#4f6ef7", "#22c55e", "#f59e0b", "#8b5cf6", "#06b6d4", "#ef4444", "#0ea5e9", "#64748b"]

export const authUser = (() => {
  try { return JSON.parse(localStorage.getItem("authUser") || "{}") } catch { return {} }
})()

// ApexCharts bar/area charts that mount while the layout is still settling (sidebar menu
// loading shifts the content width) capture the wrong container size and render squished.
// Nudging a window resize a few times after mount forces ApexCharts to recompute to the
// final dimensions. Call once near the top of each overview dashboard.
export const useChartFix = () => {
  useEffect(() => {
    const timers = [200, 500, 1000, 1800].map(ms => setTimeout(() => window.dispatchEvent(new Event("resize")), ms))
    return () => timers.forEach(clearTimeout)
  }, [])
}
export const scopeName = () => authUser?.company_context?.groups?.[0]?.name || "your business"

// small area sparkline used in KPI cards
export const Sparkline = ({ data, color, height = 56 }) => (
  <ReactApexChart
    type="area"
    height={height}
    series={[{ data }]}
    options={{
      chart: { type: "area", sparkline: { enabled: true }, height },
      stroke: { curve: "smooth", width: 2.5 },
      fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 100] } },
      colors: [color], tooltip: { enabled: false },
    }}
  />
)

// page header with title + welcome + optional right slot
export const DashHead = ({ title, welcome, right }) => (
  <div className="dk-head">
    <div>
      <h4 className="dk-title">{title} <i className="bx bxs-check-circle" /></h4>
      {welcome && <div className="dk-sub">{welcome}</div>}
    </div>
    {right}
  </div>
)

export const DateChip = ({ label = "01 May 2024 – 31 May 2024" }) => (
  <div className="dk-daterange"><i className="bx bx-calendar" /> {label} <i className="bx bx-chevron-down" /></div>
)

// the one shared stylesheet — mount once per page
export const DashStyles = () => <style>{dashCss}</style>

const dashCss = `
.dk-wrap{background:#f6f7fb;min-height:100%;}
.dk-head{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;flex-wrap:wrap;margin-bottom:18px;}
.dk-title{font-weight:700;color:#0f172a;margin:0;display:flex;align-items:center;gap:8px;font-size:22px;}
.dk-title .bxs-check-circle{color:#4f6ef7;font-size:18px;}
.dk-sub{color:#64748b;font-size:13.5px;margin-top:2px;}
.dk-daterange{display:flex;align-items:center;gap:8px;background:#fff;border:1px solid #e5e7eb;border-radius:10px;padding:8px 14px;font-size:13px;color:#334155;font-weight:600;}
.dk-card{background:#fff;border:1px solid #eef0f4;border-radius:16px;padding:18px;box-shadow:0 1px 2px rgba(16,24,40,.04);}
.dk-card-head{display:flex;justify-content:space-between;align-items:center;margin-bottom:14px;}
.dk-card-head>span:first-child{font-size:12px;font-weight:700;letter-spacing:.05em;color:#334155;}
.dk-link{border:none;background:none;color:#4f6ef7;font-weight:600;font-size:12.5px;cursor:pointer;padding:0;}
.dk-chip{font-size:12px;color:#64748b;background:#f1f5f9;border-radius:8px;padding:4px 10px;font-weight:600;}
/* KPI */
.dk-kpi{position:relative;overflow:hidden;}
.dk-kpi-label{font-size:12px;font-weight:600;color:#64748b;text-transform:uppercase;letter-spacing:.03em;}
.dk-kpi-value{font-size:22px;font-weight:700;color:#0f172a;margin-top:8px;}
.dk-kpi-delta{font-size:12px;font-weight:600;margin-top:4px;display:flex;align-items:center;gap:3px;}
.dk-kpi-delta.up{color:#16a34a;} .dk-kpi-delta.down{color:#dc2626;}
.dk-kpi-spark{margin:6px -6px -8px;}
.dk-kpi-metrics{display:flex;gap:14px;margin-top:10px;}
.dk-kpi-metrics .m{font-size:11px;color:#94a3b8;}
.dk-kpi-metrics .m b{display:block;font-size:13px;color:#334155;font-weight:700;margin-top:1px;}
/* dot + legend */
.dk-dot{width:9px;height:9px;border-radius:50%;flex:0 0 auto;display:inline-block;}
.dk-legend{display:flex;gap:16px;font-size:12.5px;color:#64748b;margin-bottom:4px;flex-wrap:wrap;}
.dk-legend span{display:flex;align-items:center;gap:6px;}
/* list */
.dk-list{list-style:none;padding:0;margin:8px 0 0;}
.dk-list li{display:flex;align-items:center;gap:8px;padding:6px 0;font-size:12.5px;}
.dk-list .nm{color:#334155;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;}
.dk-list .vl{color:#0f172a;font-weight:700;white-space:nowrap;}
/* table */
.dk-table{width:100%;border-collapse:collapse;font-size:12.5px;}
.dk-table th{text-align:left;color:#94a3b8;font-weight:600;font-size:11.5px;padding:6px 8px;border-bottom:1px solid #eef0f4;}
.dk-table td{padding:10px 8px;border-bottom:1px solid #f4f6f9;vertical-align:middle;}
.dk-badge{font-size:11px;font-weight:600;padding:3px 10px;border-radius:20px;white-space:nowrap;}
.dk-progress{height:6px;background:#eef0f4;border-radius:6px;overflow:hidden;}
.dk-progress span{display:block;height:100%;border-radius:6px;}
/* mini stat tiles */
.dk-tiles{display:grid;gap:12px;}
.dk-tile{border:1px solid #eef0f4;border-radius:14px;padding:14px;}
.dk-tile .lb{font-size:12px;color:#64748b;font-weight:600;}
.dk-tile .vl{font-size:20px;font-weight:700;color:#0f172a;margin-top:2px;}
.dk-tile .sub{font-size:11.5px;color:#94a3b8;}
.dk-gauge-center{text-align:center;margin-top:-6px;}
.dk-gauge-center b{font-size:26px;font-weight:700;color:#0f172a;}
.dk-gauge-center span{display:block;font-size:11.5px;color:#94a3b8;}
`
export default DashStyles
